import { restrictedLanguages } from '../constant/form/form'
import { ASSIGNMENT_STATUS } from '../constant/enum/assignment_status'
import { PROJECT_STATUS } from '../constant/enum/project_status'

interface PreferenceItem {
  id: number
  name: string
  preference: string
}

export const findPreference = (
  items: Pick<PreferenceItem, 'id' | 'preference'>[],
  preference: string
): number | null => {
  if (!Array.isArray(items)) return null

  return items.find(item => item.preference === preference)?.id ?? null
}

export const findPreferenceArray = (
  items: Pick<PreferenceItem, 'id' | 'preference'>[],
  preference: string
): number[] => {
  if (!Array.isArray(items)) return []

  return items
    .filter(item => item.preference === preference)
    .map(item => item.id)
}

export const filterByPreference = (
  items: PreferenceItem[],
  preference: string
): Record<string, any>[] => {
  if (!Array.isArray(items)) return []

  return items.filter(item => item.preference === preference)
}

export const getWordCount = (text = ''): number => {
  const words = text.trim().split(/\s+/g)
  if (words[0].length === 0) return 0
  return words.length
}

// converts ['twitter.com/@real_jibin', 'linkedin.com/in/jibin2706'] to {twitter: 'twitter.com/@real_jibin', linkedin: ''}
export const convertSocialLinksArrayToObject = (
  links: string[]
): Record<string, string> => {
  let count = 1
  const result: Record<string, string> = {}
  links.forEach(link => {
    if (link.includes('facebook.com')) {
      result.facebook = link
      return
    }
    if (link.includes('linkedin.com')) {
      result.linkedin = link
      return
    }
    if (link.includes('twitter.com')) {
      result.twitter = link
      return
    }
    if (link.includes('instagram.com')) {
      result.instagram = link
      return
    } else {
      result[`other_${count}`] = link
      count += 1
      return
    }
  })
  return result
}

// shallow equality check on objects
export const shallowEqual = (
  object1: Record<string, any>,
  object2: Record<string, any>
): boolean => {
  // return false if object1 or object2 contains falsy value
  if (!object1 || !object2) return false

  const keys1 = Object.keys(object1)
  const keys2 = Object.keys(object2)

  // return false if the number of keys the object contains is different
  if (keys1.length !== keys2.length) return false

  // checking keys values
  for (const key of keys1) {
    if (JSON.stringify(object1[key]) !== JSON.stringify(object2[key])) {
      return false
    }
  }

  return true
}

const months: string[] = [
  'Jan',
  'Feb',
  'March',
  'Apr',
  'May',
  'Jun',
  'July',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const formatChartDataMonthly = (
  data: ChartMetrics | undefined = {},
  role = 'writer'
): ChartActivityData[] => {
  const results: ChartActivityData[] = []
  Object.keys(data).forEach(_key => {
    const key = Number.parseInt(_key)
    // if user is writer
    if (role === 'writer') {
      const request = {
        status: 'Requests',
        month: months[key],
        week: '',
        count: data[key].request || 0
      }

      const complete = {
        status: 'Completed',
        month: months[key],
        week: '',
        count: data[key].completed || 0
      }

      const approved = {
        status: 'Approved',
        month: months[key],
        week: '',
        count: data[key].passed || 0
      }
      results.push(request, complete, approved)
    } else {
      // if user editor
      const wordCount = {
        status: 'Word count',
        month: months[key],
        week: '',
        count: data[key].word_count || 0
      }

      const wordCountPerDay = {
        status: 'Word count per day',
        month: months[key],
        week: '',
        count: data[key].word_count_per_day || 0
      }
      results.push(wordCount, wordCountPerDay)
    }
  })
  return results
}

export const formatChartDataWeekly = (
  data: ChartWeeklyMetrics | undefined = {},
  role = 'writer'
): ChartActivityData[] => {
  const results: ChartActivityData[] = []
  Object.keys(data).forEach(_key => {
    const _month = Number.parseInt(_key)
    Object.keys(data[_month]).forEach(__key => {
      const _week = Number.parseInt(__key)
      // if user is writer
      if (role === 'writer') {
        const request = {
          status: 'Requests',
          month: months[_month],
          week: `Week ${_week}`,
          count: data[_month][_week].request || 0
        }

        const complete = {
          status: 'Completed',
          month: months[_month],
          week: `Week ${_week}`,
          count: data[_month][_week].completed || 0
        }

        const approved = {
          status: 'Approved',
          month: months[_month],
          week: `Week ${_week}`,
          count: data[_month][_week].passed || 0
        }
        results.push(request, complete, approved)
      } else {
        // is user is editor
        const wordCount = {
          status: 'Word count',
          month: months[_month],
          week: `Week ${_week}`,
          count: data[_month][_week].word_count || 0
        }

        const wordCountPerDay = {
          status: 'Word count per day',
          month: months[_month],
          week: `Week ${_week}`,
          count: data[_month][_week].word_count_per_day || 0
        }
        results.push(wordCount, wordCountPerDay)
      }
    })
  })
  return results
}

// https://stackoverflow.com/questions/49634850/javascript-convert-plain-text-links-to-clickable-links
export function linkify(inputText: string): string {
  let replacedText

  //URLs starting with http://, https://, or ftp://
  const replacePattern1 =
    /(\b(https?|ftp):\/\/[\w!#%&+,./:;=?@|~-]*[\w#%&+/=@|~-])/gim
  replacedText = inputText.replace(
    replacePattern1,
    '<a href="$1" target="_blank">$1</a>'
  )

  //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
  const replacePattern2 = /(^|[^/])(www\.\S+(\b|$))/gim
  replacedText = replacedText.replace(
    replacePattern2,
    '$1<a href="http://$2" target="_blank">$2</a>'
  )

  //Change email addresses to mailto:: links.
  const replacePattern3 = /(([\w.\\-])+@[_a-z]+?(\.[a-z]{2,6})+)/gim
  replacedText = replacedText.replace(
    replacePattern3,
    '<a href="mailto:$1" target="_blank">$1</a>'
  )

  return replacedText
}

export const getRestrictedLanguages = (
  languages: { id: number; title: string }[] = [],
  restrictedLang: string[] = restrictedLanguages
): { id: number; title: string }[] => {
  if (restrictedLang.length === 0) return languages
  const _languages: { id: number; title: string }[] = []
  restrictedLang.forEach(lang => {
    const _match = languages.find(
      _lang => _lang.title.toLowerCase() === lang.toLowerCase()
    )
    if (_match) _languages.push(_match)
  })
  return _languages
}

/** Get Role Base Assignments status */
export const getCategoryBaseAssignmentStatus = (
  category: ProjectCategory,
  status: AssignmentStatus
): {
  id: AssignmentStatus
  title: string
  title_extended: string
  color: string
  stage: string
} => {
  if (category === 'translation') {
    const assignmentStatus = { ...ASSIGNMENT_STATUS[status] }
    /** Change status title and extended title */
    switch (status) {
      case ASSIGNMENT_STATUS.WRI_AWA.id:
        assignmentStatus.title = 'ATA'
        assignmentStatus.title_extended = 'ATA'
        break
      case ASSIGNMENT_STATUS.WRI_WRK.id:
        assignmentStatus.title = 'Translating'
        assignmentStatus.title_extended = 'Translating'
        break
      case ASSIGNMENT_STATUS.EDO_AWA.id:
      case ASSIGNMENT_STATUS.EDO_RJT.id:
        assignmentStatus.title = 'Translated'
        assignmentStatus.title_extended = 'Translated'
        break
      default:
        break
    }
    return assignmentStatus
  } else if (category === 'design') {
    const assignmentStatus = { ...ASSIGNMENT_STATUS[status] }
    /** Change status title and extended title */
    switch (status) {
      case ASSIGNMENT_STATUS.WRI_AWA.id:
        assignmentStatus.title = 'ADA'
        assignmentStatus.title_extended = 'ADA'
        break
      case ASSIGNMENT_STATUS.WRI_WRK.id:
        assignmentStatus.title = 'Designing'
        assignmentStatus.title_extended = 'Designing'
        break
      case ASSIGNMENT_STATUS.EDO_AWA.id:
      case ASSIGNMENT_STATUS.EDO_RJT.id:
        assignmentStatus.title = 'Designed'
        assignmentStatus.title_extended = 'Designed'
        break
      default:
        break
    }
    return assignmentStatus
  } else if (category === 'others') {
    const assignmentStatus = { ...ASSIGNMENT_STATUS[status] }

    switch (status) {
      case ASSIGNMENT_STATUS.WRI_AWA.id:
        assignmentStatus.title = 'ACA'
        assignmentStatus.title_extended = 'ACA'
        break
      case ASSIGNMENT_STATUS.WRI_WRK.id:
        assignmentStatus.title = 'Creating'
        assignmentStatus.title_extended = 'Creating'
        break
      case ASSIGNMENT_STATUS.EDO_AWA.id:
      case ASSIGNMENT_STATUS.EDO_RJT.id:
        assignmentStatus.title = 'Created'
        assignmentStatus.title_extended = 'Created'
        break
      default:
        break
    }

    return assignmentStatus
  } else {
    return ASSIGNMENT_STATUS[status]
  }
}

export const getCategoryBaseProjectStatus = (
  category: ProjectCategory,
  status: ProjectStatus
): {
  id: string
  title: string
  client_title: string
  color: string
} => {
  if (category === 'design') {
    const projectStatus = { ...PROJECT_STATUS[status] }

    if (status === PROJECT_STATUS.writing_progress.id) {
      projectStatus.title = 'Creation Progress'
      projectStatus.client_title = 'In Progress (Designing)'
    }

    if (status === PROJECT_STATUS.editing_progress.id) {
      projectStatus.title = 'Designed'
      projectStatus.client_title = 'Designed'
    }

    return projectStatus
  }

  return PROJECT_STATUS[status]
}

export function loadScript(src: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')

    script.src = src
    script.async = true
    script.onload = () => resolve(true)
    script.onerror = () => reject(false)

    document.body.appendChild(script)
  })
}

export function parseUrlString(urlString: string): string[] {
  if (!urlString.includes(':~:text=')) {
    const result = urlString
      .split(',')
      .map((link: string) => link.trim())
      .filter(Boolean) // filter out falsy values

    return [...new Set(result)]
  }

  let result = urlString
    .split(/http|https/)
    .map((link: string) => link.trim())
    .filter(Boolean) // filter out falsy values
    .map((link: string) => {
      // remove last comma
      if (link.endsWith(',')) return `http${link.replace(/,$/, '')}`

      // append http if link starts with s:// or ://
      if (link.startsWith('s://') || link.startsWith('://'))
        return `http${link}`

      return link
    })

  // remove duplicates urls from an array
  result = [...new Set(result)]

  return result
}

interface EditorModifiedFeedback {
  data: Feedbacks
  rework_areas: { id: number; title: string; inputs: number[]; key: string }[]
  feedback_inputs: { id: number; title: string }[]
  userRole?: string
  isOpsRole?: boolean
}

/* Modifying data to simplify on the front-end */
export const getModifiedEditorFeedbackData = ({
  data,
  rework_areas,
  feedback_inputs,
  userRole,
  isOpsRole = false
}: EditorModifiedFeedback): ModifiedEditorFeedback[] => {
  const list: ModifiedEditorFeedback[] = []

  /* reversing the object to display  scores */
  const reverseScoresData = (scores: Record<string, number | null> = {}) => {
    return {
      grammar: scores?.language ?? 0,
      irrelevant: scores?.content ?? 0,
      contextuality: scores?.content ?? 0,
      plagiarism: scores?.plagiarism ?? 0,
      authenticity: scores?.plagiarism ?? 0,
      outline: scores?.guideline ?? 0,
      readability: scores?.readability ?? 0
    }
  }
  data.feedback.forEach((_feedback: EditorFeedback) => {
    /* taking a temporary object to display it on front-end */
    const obj: ModifiedEditorFeedback = {
      feedback_text: _feedback.feedback_text,
      full_name: _feedback.full_name,
      status: _feedback.status,
      avatar: _feedback.avatar,
      date_created: _feedback?.date,
      feedbacks: [],
      status_code: _feedback.status_code,
      reason: _feedback.reason ?? '',
      rework_attachments: _feedback.rework_attachments ?? [],
      is_ops_team: _feedback?.is_ops_team,
      scores: _feedback?.scores,
      flagged_creator: _feedback?.flagged_creator,
      flag_reason: _feedback.flag_reason,
      is_positive_feedback: _feedback.is_positive_feedback,
      feedback_tag_type: _feedback.feedback_tag_type
    }
    _feedback.rework_areas_data.forEach(
      (area: { id: number; inputs: number[] }) => {
        //finding rework_areas_data id from rework_areas
        //filtering out rework area with key flag_creator
        const _parentItem = rework_areas.find(item => {
          if (!isOpsRole && userRole !== 'editor') {
            return item.key !== 'flag_creator' && item.id === area.id
          }
          return item.id === area.id
        })

        // fetching details from _parentItem to get scores,title and issues
        if (_parentItem) {
          const feedbackScores: Record<string, number | null> =
            reverseScoresData(_feedback.scores)
          const _score = feedbackScores?.[_parentItem.key] ?? 0
          const _issues: string[] = []
          area.inputs.forEach((id: number) => {
            const _issue =
              feedback_inputs.find(_input => _input.id === id)?.title ?? ''
            _issues.push(_issue)
          })
          obj.feedbacks.push({
            title: _parentItem.title,
            score: _score,
            issues: _issues
          })
        }
      }
    )
    //pushing the obj with updated values inside list
    list.push(obj)
  })
  return list
}

// get modified timeline object
export const getModifiedTimelineFeedbackData = ({
  data: timeline,
  rework_areas,
  feedback_inputs
}: {
  data: Timeline
  rework_areas: { id: number; title: string; inputs: number[]; key: string }[]
  feedback_inputs: { id: number; title: string }[]
}): ModifiedEditorFeedback => {
  /* reversing the object to display  scores */
  const reverseScoresData = (scores: Record<string, number | null> = {}) => {
    return {
      grammar: scores?.language ?? 0,
      irrelevant: scores?.content ?? 0,
      contextuality: scores?.content ?? 0,
      plagiarism: scores?.plagiarism ?? 0,
      authenticity: scores?.plagiarism ?? 0,
      outline: scores?.guideline ?? 0,
      readability: scores?.readability ?? 0
    }
  }

  /* taking a temporary object to display it on front-end */
  const obj: ModifiedEditorFeedback = {
    feedback_text: timeline.feedback ?? '',
    full_name: timeline.assigner_details?.full_name ?? '',
    status: 'feedback',
    avatar: timeline.assigner_details?.avatar ?? '',
    date_created: timeline?.created_at,
    feedbacks: [],
    status_code: timeline.status_code,
    reason: timeline.reason ?? '',
    rework_attachments: timeline.rework_attachments ?? []
  }

  timeline.rework_areas_data.forEach(
    (area: { id: number; inputs: number[] }) => {
      //finding rework_areas_data id from rework_areas
      const _parentItem = rework_areas.find(item => item.id === area.id)

      // fetching details from _parentItem to get scores,title and issues
      if (_parentItem) {
        const feedbackScores: Record<string, number | null> = reverseScoresData(
          timeline.scores
        )
        const _score = feedbackScores?.[_parentItem.key] ?? 0
        const _issues: string[] = []
        area.inputs.forEach((id: number) => {
          const _issue =
            feedback_inputs.find(_input => _input.id === id)?.title ?? ''
          _issues.push(_issue)
        })
        obj.feedbacks.push({
          title: _parentItem.title,
          score: _score,
          issues: _issues
        })
      }
    }
  )

  return obj
}

// At least one rating should be less than or equal to 3 to mark as rework
export const validateRatingScores = (
  values: Record<string, string>,
  type: ButtonActionKeys | null
): boolean => {
  if (type === 'rework') {
    const _scores = [
      values.grammar,
      values.irrelevant,
      values.contextuality,
      values.plagiarism,
      values.authenticity,
      values.outline,
      values.readability
    ]
    const relevantScores = _scores.some(score => Number.parseInt(score) <= 3)
    if (!relevantScores) {
      return false
    }
    return true
  }
  return true
}

export const getVerticalKey = (
  selectedVerticalId: number | null,
  verticals: VerticalObjectType[]
): string => {
  return (
    verticals.find(vertical => vertical.id === selectedVerticalId)?.key ?? ''
  )
}

export const getProjectCategory = (
  categoryId: number | null,
  project_categories: {
    id: number
    title: string
    key: string
    icon: string
    disabled?: boolean
  }[]
): string => {
  return (
    project_categories.find(category => category.id === categoryId)?.key ??
    'text'
  )
}

export const generateReferenceFilesWithTags = (
  value: AssignmentListFormValues,
  fields: BackendFormField[]
): { id: string; tag: string }[] => {
  const formFieldIds = [
    'reference_files_design_content',
    'reference_files_other_references',
    'reference_files_artworks',
    'reference_files_infographic_description',
    'reference_files_blog_content',
    'adapts_main_design',
    'references_brand_style_guide',
    'references_logo_files'
  ]

  const result: { id: string; tag: string }[] = []

  formFieldIds.forEach(formId => {
    if (value?.[formId]?.attachments === undefined) {
      Array.isArray(value?.[formId]) &&
        value?.[formId]?.forEach((file: Attachment) => {
          result.push({
            id: file.id,
            tag: fields?.find(field => field.id === formId)?.tag ?? ''
          })
        })
      return
    }
    value?.[formId]?.attachments.forEach((file: Attachment) => {
      result.push({
        id: file.id,
        tag: fields?.find(field => field.id === formId)?.tag ?? ''
      })
    })
  })

  return result
}

export const generateReferenceFilesWithTagsProject = (
  value: BrandGuidelines,
  formFields: {
    assignment?: BackendFormField[]
    project?: BackendFormField[]
    brandGuidelines?: BackendFormField[]
  }
): { id: string; tag: string }[] => {
  const formFieldIds = ['references_logo_files', 'references_brand_style_guide']

  const result: {
    id: string
    tag: string
    name?: string
    type?: string
  }[] = []

  formFieldIds.forEach(formId => {
    const _attachments = value?.[formId] as CommentBox
    _attachments?.attachments?.forEach((file: Attachment) => {
      result.push({
        id: file.id,
        tag:
          formFields?.brandGuidelines?.find(assign => assign.id === formId)
            ?.tag ?? '',
        name: file.name,
        type: ''
      })
    })
  })

  return result
}

/* filter options according to the design styles
   i.e options will be filtered based on
   ids (illustrated,image,typography) selected by the user */
export const getFilteredVisualOptions = (
  options: BlockSelectOption[],
  selectedDesignType: string | null
): BlockSelectOption[] => {
  if (selectedDesignType) {
    return options.filter(
      option => option?.type?.toLowerCase() === selectedDesignType.toLowerCase()
    )
  }
  return options
}

// Get rework reasons
export const getReworkReasons = (category?: ProjectCategory): string[] => {
  switch (category) {
    case 'design':
      return [
        'additional_feedback',
        'design_layout_and_color',
        'design_content_copy_text',
        'design_visual_elements',
        'design_originality',
        'design_brief_guidelines'
      ]
    case 'text':
      return [
        'additional_feedback',
        'grammar',
        'irrelevant',
        'outline',
        'initiative'
      ]
    case 'translation':
      return [
        'additional_feedback',
        'grammar',
        'contextuality',
        'authenticity',
        'readability',
        'outline'
      ]
    case 'others':
      return []
    default:
      return []
  }
}

export const getFirstLetterFromName = (
  firstName: string,
  lastNames: string
): string => {
  const firstNameLetter = firstName.charAt(0) || ''
  const lastNamesLetter = lastNames.charAt(0) || ''
  return `${firstNameLetter.toUpperCase()}${lastNamesLetter.toUpperCase()}`
}

export const getFullName = (firstName: string, lastName: string): string => {
  if (!firstName) return lastName
  if (!lastName) return firstName

  return `${firstName} ${lastName}`
}

// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
export function formatBytesToHumanReadableString(
  bytes: number,
  decimals = 2
): string {
  if (bytes === 0) return '0 Bytes'

  const kiloBytes = 1024
  const fractionDigits = decimals < 0 ? 0 : decimals
  const sizes = ['bytes', 'kb', 'mb', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const result = Math.floor(Math.log(bytes) / Math.log(kiloBytes))

  return (
    Number.parseFloat(
      (bytes / Math.pow(kiloBytes, result)).toFixed(fractionDigits)
    ) + sizes[result]
  )
}

export const areTwoArrayValuesSame = <T>(
  firstArray: T[],
  secondArray: T[],
  equalityChecker = (item1: T, item2: T) => item1 === item2
): boolean => {
  if (
    !Array.isArray(firstArray) ||
    !Array.isArray(secondArray) ||
    firstArray.length !== secondArray.length
  )
    return false
  return firstArray.every((firstItem, index) => {
    const secondItem = secondArray[index]
    return equalityChecker(firstItem, secondItem)
  })
}

export function onlyUnique<T>(value: T, index: number, self: T[]): boolean {
  return self.indexOf(value) === index
}

type Portals = 'operations' | 'business' | 'creators'
// This doesn't work for amplify links currently
export const getRedirectLinkOriginString = ({
  currentHostname,
  currentOrigin,
  portalForRedirection,
  currentPortalName
}: {
  currentOrigin: string
  currentHostname: string
  portalForRedirection: Portals
  currentPortalName: Portals
}): string => {
  const portalToPortsMapping: Record<Portals, string> = {
    operations: '3002',
    business: '3001',
    creators: '3000'
  }
  let redirectLink
  if (currentHostname === 'localhost') {
    redirectLink = currentOrigin.replace(
      portalToPortsMapping[currentPortalName],
      portalToPortsMapping[portalForRedirection]
    )
  } else if (currentHostname.includes('.pages.dev')) {
    let _currentPortalName: string = currentPortalName
    if (_currentPortalName === 'operations') {
      _currentPortalName = 'operation'
    } else if (_currentPortalName === 'creators') {
      _currentPortalName = 'creator'
    }
    redirectLink = currentOrigin.replace(
      _currentPortalName,
      portalForRedirection
    )
  } else {
    redirectLink = currentOrigin.replace(
      currentPortalName,
      portalForRedirection
    )
  }
  return redirectLink
}

export const getShouldDisableField = (fieldValue: any) => {
  if ([null, undefined, ''].includes(fieldValue)) return false
  if (Array.isArray(fieldValue) && fieldValue.length === 0) return false
  if (
    !Array.isArray(fieldValue) &&
    typeof fieldValue === 'object' &&
    Object.keys(fieldValue).length === 0
  )
    return false
  if (
    !Array.isArray(fieldValue) &&
    typeof fieldValue === 'object' &&
    Object.keys(fieldValue).length > 0
  ) {
    if (getShouldDisableField(fieldValue.comment)) return true
    if (getShouldDisableField(fieldValue.richText)) return true
    if (getShouldDisableField(fieldValue.richText)) return true
    if (getShouldDisableField(fieldValue.loomUrls)) return true
    return false
  }
  return true
}

export function roundNumberToGivenDecimalPlaces(
  val: number,
  decimals: number
): number {
  return +`${Math.round(+`${val.toFixed(decimals)}e+${decimals}`)}e-${decimals}`
}
